import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Check, Zap, Shield, Feather, Users, Bookmark } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { trackButtonClick } from '../utils/analytics';

const LandingPage = () => {
  const features = [
    {
      icon: <Feather className="h-6 w-6 text-blue-600" />,
      title: "AI-Powered Prompt Improvement",
      description: "Transform your prompts with intelligent suggestions and optimizations powered by advanced AI models."
    },
    {
      icon: <Shield className="h-6 w-6 text-green-600" />,
      title: "Zero-Knowledge Encryption",
      description: "Your data is encrypted client-side with your password. Even we can't access your prompts - total privacy guaranteed."
    },
    {
      icon: <Zap className="h-6 w-6 text-purple-600" />,
      title: "Organized Project Management",
      description: "Keep your prompts organized in projects with sticky note-style displays. Never lose track of your best work."
    },
    {
      icon: <Users className="h-6 w-6 text-orange-600" />,
      title: "Custom System Prompts",
      description: "Create and manage your own system prompts for specialized use cases and consistent AI interactions."
    },
    {
      icon: <Bookmark className="h-6 w-6 text-indigo-600" />,
      title: "Quick Footnotes",
      description: "Save reusable text snippets like file paths or coding preferences, then instantly append them to any prompt with one click."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "AI Researcher",
      content: "ItsMyVibe has revolutionized how I manage and improve my prompts. The AI suggestions are incredibly helpful.",
      rating: 5
    },
    {
      name: "Marcus Rodriguez",
      role: "Content Creator",
      content: "The encryption gives me peace of mind, and the organization features help me stay productive.",
      rating: 5
    },
    {
      name: "Dr. Emily Watson",
      role: "Data Scientist",
      content: "Best prompt management tool I've used. The project organization and AI improvements are game-changers.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Feather className="h-5 w-5 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              ItsMyVibe
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="ghost" onClick={() => trackButtonClick('sign_in', 'navigation')}>Sign In</Button>
            </Link>
            <Link to="/register">
              <Button onClick={() => trackButtonClick('get_started', 'navigation')}>Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  Craft Better{' '}
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    AI Prompts
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Organize, improve, and manage your AI prompts with intelligent suggestions, 
                  zero-knowledge encryption, and seamless project organization.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" onClick={() => trackButtonClick('start_free_today', 'hero')}>
                    Start Free Today
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" onClick={() => trackButtonClick('try_demo', 'hero')}>
                    Try Demo Account
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>End-to-end encrypted</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>Free forever</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?q=80&w=1920&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="AI-Powered Prompt Engineering - Writing and Technology"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              
              {/* Floating cards */}
              <div className="absolute -top-6 -left-6 bg-white rounded-lg p-4 shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium">AI Analyzing...</span>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold">Everything You Need</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed for prompt engineers, content creators, and AI enthusiasts.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold">Stop Losing Your Best Prompts</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Tired of scattered Notepad files, lost Word docs, and retyping the same prompts over and over?
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            {/* Problems */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-red-300">The Old Way Is Broken</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-red-400 text-sm">✗</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">Scattered & Disorganized</h4>
                    <p className="text-slate-400 text-sm">Multiple Notepad files, Word docs, random text files - where did you save that perfect prompt?</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-red-400 text-sm">✗</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">Constant Retyping</h4>
                    <p className="text-slate-400 text-sm">Prompt doesn't work in Claude/Cursor/Gemini? Start over. Copy-paste from random files. Waste time.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-red-400 text-sm">✗</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">Manual Context Every Time</h4>
                    <p className="text-slate-400 text-sm">Manually adding environment paths, .env instructions, project context to every single prompt.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-red-400 text-sm">✗</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">No Improvement Process</h4>
                    <p className="text-slate-400 text-sm">Drafting prompts in isolation with no systematic way to make them better.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Solutions */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-green-300">The ItsMyVibe Way</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">Organized Project Structure</h4>
                    <p className="text-slate-400 text-sm">All prompts organized by project. Visual cards. Search instantly. Never lose a prompt again.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">One-Click Copy & Paste</h4>
                    <p className="text-slate-400 text-sm">Perfect for Claude Code, Cursor, Gemini CLI. Copy optimized prompts directly to your workflow.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">Smart System Prompts</h4>
                    <p className="text-slate-400 text-sm">Add default context once. AI improvements automatically include your environment, .env handling, project specifics.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-1">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-200">AI-Powered Brainstorming</h4>
                    <p className="text-slate-400 text-sm">Draft rough ideas, let AI transform them into professional, effective prompts. Iterate until perfect.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/register">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 text-lg" onClick={() => trackButtonClick('stop_wasting_time', 'problem_solution')}>
                Stop Wasting Time - Start Organizing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-slate-400 mt-4 text-sm">Join developers who've already organized 10,000+ prompts</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold">How It Works</h2>
            <p className="text-xl text-gray-600">Get started in minutes</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold">Create Projects</h3>
              <p className="text-gray-600">Organize your prompts into projects and categories for easy management.</p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-xl font-semibold">Write & Improve</h3>
              <p className="text-gray-600">Use AI-powered suggestions to craft and refine your prompts for better results.</p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-semibold">Deploy & Scale</h3>
              <p className="text-gray-600">Copy optimized prompts and use them across your AI workflows and applications.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold">Loved by Creators</h2>
            <p className="text-xl text-gray-600">See what our users are saying</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index}>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4">"{testimonial.content}"</p>
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto max-w-4xl text-center text-white space-y-8">
          <h2 className="text-4xl font-bold">Ready to Transform Your Prompts?</h2>
          <p className="text-xl opacity-90">
            Join thousands of creators and researchers who trust ItsMyVibe for their prompt engineering needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" onClick={() => trackButtonClick('start_free_today', 'final_cta')}>
                Start Free Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600" onClick={() => trackButtonClick('try_demo', 'final_cta')}>
                Try Demo Account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Feather className="h-5 w-5 text-white" />
              </div>
              <span className="text-2xl font-bold">ItsMyVibe</span>
            </div>
            <div className="text-gray-400 text-sm">
              © 2025 ItsMyVibe. Crafted with ❤️ for prompt engineers everywhere.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;